<template>
	<div id="forum-management-container">
		<div class="forum-content">
			<div class="forum-stats" v-if="!showSkeleton && boardList.length">
				<div class="stat-tile">
					<div class="stat-label">Total boards</div>
					<div class="stat-value">{{ stats.total }}</div>
				</div>
				<div class="stat-tile">
					<div class="stat-label">Active</div>
					<div class="stat-value">{{ stats.active }}</div>
				</div>
				<div class="stat-tile">
					<div class="stat-label">Inactive</div>
					<div class="stat-value">{{ stats.inactive }}</div>
				</div>
			</div>

			<div class="header-actions">
				<div class="search-container">
					<input
						type="text"
						placeholder="Search by board ID, course ID, course title, or scope (global / course)"
						v-model="searchQuery"
						class="search-input" />
				</div>
			</div>

			<div class="table-container">
				<SkeletonTableLoader v-if="showSkeleton" :rowCount="7" :columnCount="8" />

				<div v-else-if="!showSkeleton && filteredBoards.length === 0" class="no-data-container">
					<p class="no-data-text">No boards visible to you yet.</p>
				</div>

				<table v-else>
					<thead>
						<tr>
							<th>ID</th>
							<th>Title</th>
							<th>Scope</th>
							<th>Reference</th>
							<th>Status</th>
							<th>Students Can Post</th>
							<th>Created</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="board in filteredBoards" :key="board.isPlaceholder ? `placeholder-${board.scope}-${board.entityID}` : board.id">
							<td>{{ board.id != null ? board.id : "—" }}</td>
							<td class="title-cell" :title="formatTitle(board)">{{ formatTitle(board) }}</td>
							<td>
								<span class="scope-pill" :class="`scope-${(board.scope || '').toLowerCase()}`">
									{{ formatScope(board) }}
								</span>
							</td>
							<td>{{ board.entityID != null ? board.entityID : "—" }}</td>
							<td>
								<span class="status-pill" :class="statusPillClass(board)">
									{{ formatStatus(board) }}
								</span>
							</td>
							<td>
								<i v-if="board.isStudentThreadsAllowed" class="fas fa-check allow-on" title="Students can post"></i>
								<i v-else class="fas fa-ban allow-off" title="Students cannot post"></i>
							</td>
							<td>{{ formatDateTime(board.createdAt) }}</td>
							<td class="row-actions">
								<button
									v-if="!board.isPlaceholder"
									class="open-btn"
									@click="openBoard(board)"
								>
									<i class="fas fa-arrow-right"></i>
									OPEN
								</button>
								<button
									v-if="canEnableRow(board)"
									class="enable-btn"
									:disabled="isRowSubmitting(board)"
									@click="enableRow(board)"
								>
									<i class="fas fa-power-off"></i>
									ENABLE
								</button>
								<button
									v-if="canDisableRow(board)"
									class="disable-btn"
									:disabled="isRowSubmitting(board)"
									@click="disableRow(board)"
								>
									<i class="fas fa-pause-circle"></i>
									DISABLE
								</button>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import SkeletonTableLoader from "@/components/Shared/SkeletonTableLoader.vue";
import { formatToDDMMMYY_HHmm } from "../../helpers/FormatHelpers";
import { UserType } from "../../constants/user";
import { BoardScope, BoardStatus } from "../../constants/forum";

export default {
	name: "ForumManagement",
	components: { SkeletonTableLoader },
	data() {
		return {
			searchQuery: "",
			isLoading: false,
			// Per-row busy flags keyed by `${scope}:${entityID}`.
			submittingKeys: {},
		};
	},
	computed: {
		...mapGetters("forum", ["visibleBoards"]),
		...mapGetters("productVariant", ["allProductVariants"]),
		...mapState("user", ["user"]),
		// Gate skeleton on productVariant load so the table renders once
		// instead of placeholders folding in after real boards.
		...mapState("productVariant", { isProductVariantsLoading: "loading" }),
		showSkeleton() {
			if (this.isLoading) return true;
			if (this.isManagement && this.isProductVariantsLoading) return true;
			return false;
		},
		isTrainer() {
			if (!this.user || !this.user.type) return false;
			return String(this.user.type).toLowerCase() === UserType.TRAINER.toLowerCase();
		},
		isManagement() {
			if (!this.user || !this.user.type) return false;
			return String(this.user.type).toLowerCase() === UserType.MANAGEMENT.toLowerCase();
		},
		// Management sees every tenant variant; Trainer only their assignments.
		candidateProductVariants() {
			if (this.isManagement) return this.allProductVariants || [];
			if (this.isTrainer) return (this.user && this.user.productVariants) || [];
			return [];
		},
		realBoards() {
			return this.visibleBoards?.items || [];
		},
		// Group real boards by scope and normalize entity ids to numbers so
		// the placeholder dedup Set never misses on string-vs-number drift.
		realBoardsByScope() {
			const globalBoards = [];
			const courseBoardEntityIds = new Set();
			for (const board of this.realBoards) {
				const scope = (board.scope || "").toLowerCase();
				if (scope === "global") {
					globalBoards.push(board);
				} else if (scope === "productvariant") {
					const normalizedEntityId = this.normalizeEntityId(board.entityID);
					if (normalizedEntityId != null) courseBoardEntityIds.add(normalizedEntityId);
				}
			}
			return { globalBoards, courseBoardEntityIds };
		},
		// Synthesize placeholder rows for boards that don't exist yet so
		// management/trainer have a row to click "Enable" on. Skip when a
		// real board already covers the same scope+entity.
		placeholderBoards() {
			const { globalBoards, courseBoardEntityIds } = this.realBoardsByScope;
			const placeholders = [];

			if (globalBoards.length === 0) {
				placeholders.push(this.buildPlaceholderBoard("global", null));
			}

			const seenPlaceholderEntityIds = new Set();
			for (const variant of this.candidateProductVariants) {
				const courseId = this.normalizeEntityId(variant && variant.id);
				if (courseId == null) continue;
				if (courseBoardEntityIds.has(courseId)) continue;
				if (seenPlaceholderEntityIds.has(courseId)) continue;
				seenPlaceholderEntityIds.add(courseId);
				placeholders.push(this.buildPlaceholderBoard("productvariant", courseId));
			}

			return placeholders;
		},
		boardList() {
			return [...this.realBoards, ...this.placeholderBoards];
		},
		productVariantTitleByEntityId() {
			const map = new Map();
			for (const variant of this.candidateProductVariants) {
				const courseId = this.normalizeEntityId(variant && variant.id);
				if (courseId == null) continue;
				if (variant.title) map.set(courseId, String(variant.title));
			}
			return map;
		},
		// Stats reflect the search-filtered rows so tiles match what's visible.
		// Inactive rolls placeholders + real-inactive together (both = no posting).
		stats() {
			const boards = this.filteredBoards;
			let active = 0, inactive = 0;
			for (const board of boards) {
				if (this.isBoardActive(board)) active++;
				else inactive++;
			}
			return { total: boards.length, active, inactive };
		},
		filteredBoards() {
			const queryText = (this.searchQuery || "").toLowerCase().trim();
			if (!queryText) return this.boardList;
			return this.boardList.filter((board) => {
				const haystackParts = [
					board.id != null ? String(board.id) : null,
					board.entityID != null ? String(board.entityID) : null,
					this.formatTitle(board),
					this.formatScope(board),
					board.scope || null,
				];
				for (const part of haystackParts) {
					if (part && String(part).toLowerCase().includes(queryText)) return true;
				}
				return false;
			});
		},
	},
	methods: {
		...mapActions("forum", ["fetchVisibleBoards", "createBoard", "setBoardStatus"]),
		...mapActions("user", ["fetchCurrentUser"]),
		isBoardActive(board) {
			if (!board || board.isPlaceholder) return false;
			return String(board.status || "").toLowerCase() === BoardStatus.Active.toLowerCase();
		},
		formatStatus(board) {
			if (!board) return "—";
			if (board.isPlaceholder) return "Not enabled";
			return this.isBoardActive(board) ? "Active" : "Inactive";
		},
		statusPillClass(board) {
			if (!board) return "status-pill-unknown";
			if (board.isPlaceholder) return "status-pill-not-enabled";
			return this.isBoardActive(board) ? "status-pill-active" : "status-pill-inactive";
		},
		rowKey(board) {
			const entity = board.entityID == null ? "global" : String(board.entityID);
			return `${(board.scope || "").toLowerCase()}:${entity}`;
		},
		isRowSubmitting(board) {
			return !!this.submittingKeys[this.rowKey(board)];
		},
		canToggleBoard(board) {
			if (String(board.scope || "").toLowerCase() === BoardScope.Global.toLowerCase()) return this.isManagement;
			return true;
		},
		canEnableRow(board) {
			if (this.isBoardActive(board)) return false;

			return this.canToggleBoard(board);
		},
		canDisableRow(board) {
			if (!this.isBoardActive(board)) return false;

			return this.canToggleBoard(board);
		},
		async enableRow(board) {
			const key = this.rowKey(board);
			if (this.submittingKeys[key]) return;
			this.$set(this.submittingKeys, key, true);
			try {
				if (board.isPlaceholder) {
					await this.createBoard({
						boardScope: board.scope,
						entityId: board.entityID,
					});
				} else {
					await this.setBoardStatus({
						boardScope: board.scope,
						entityId: board.entityID,
						status: BoardStatus.Active,
					});
				}
				await this.fetchVisibleBoards();
				this.showSuccess("Forum is now enabled.");
			} catch (err) {
				this.showError(err?.message || "Failed to enable forum.");
			} finally {
				this.$set(this.submittingKeys, key, false);
			}
		},
		async disableRow(board) {
			const key = this.rowKey(board);
			if (this.submittingKeys[key]) return;
			this.$set(this.submittingKeys, key, true);
			try {
				await this.setBoardStatus({
					boardScope: board.scope,
					entityId: board.entityID,
					status: BoardStatus.Inactive,
				});
				await this.fetchVisibleBoards();
				this.showSuccess("Forum disabled. Existing posts stay visible.");
			} catch (err) {
				this.showError(err?.message || "Failed to disable forum.");
			} finally {
				this.$set(this.submittingKeys, key, false);
			}
		},
		showSuccess(message, options = {}) {
			this.$toasted.success(message, { icon: "check", theme: "outline", position: "bottom-center", duration: 3000, ...options });
		},
		// Refetch when missing, or when a Trainer has no productVariants —
		// some login flows hydrate the user without populating assignments.
		async ensureCurrentUserLoaded() {
			const userMissing = !this.user;
			const trainerVariantsMissing =
				this.isTrainer && !Array.isArray(this.user.productVariants);
			if (!userMissing && !trainerVariantsMissing) return;
			try {
				await this.fetchCurrentUser();
			} catch (err) {
				this.showError(err?.message || "Unable to load current user.");
			}
		},
		showError(message, options = {}) {
			this.$toasted.error(message, { icon: "exclamation-triangle", theme: "outline", position: "bottom-center", singleton: true, duration: 3000, ...options });
		},
		async getBoards() {
			try {
				this.isLoading = true;
				await this.fetchVisibleBoards();
			} catch (err) {
				this.showError(err?.message || "Unable to load boards.");
			} finally {
				this.isLoading = false;
			}
		},
		normalizeEntityId(rawValue) {
			if (rawValue == null) return null;
			const numericValue = Number(rawValue);
			return Number.isFinite(numericValue) ? numericValue : null;
		},
		// Mirrors real-board shape so the table template can render rows
		// without branching on isPlaceholder per cell.
		buildPlaceholderBoard(scope, entityID) {
			return {
				id: null,
				scope,
				entityID,
				isStudentThreadsAllowed: true,
				createdAt: null,
				isPlaceholder: true,
			};
		},
		formatScope(board) {
			if (!board.scope) return "—";
			if (board.scope.toLowerCase() === "global") return "Global";
			if (board.scope.toLowerCase() === "productvariant") return "Course";
			return board.scope;
		},
		formatTitle(board) {
			const scope = (board.scope || "").toLowerCase();
			if (scope === "global") return "Global";
			if (scope === "productvariant") {
				const courseId = this.normalizeEntityId(board.entityID);
				const title = this.productVariantTitleByEntityId.get(courseId);
				if (title) return title;
			}
			return "—";
		},
		formatDateTime(date) {
			if (!date) return "—";
			return formatToDDMMMYY_HHmm(date);
		},
		openBoard(board) {
			const scope = (board.scope || "").toLowerCase();
			const params = { scope };
			if (board.entityID != null) params.entityId = board.entityID;
			this.$router.push({ name: "ForumBoardAdmin", params });
		},
	},
	mounted() {
		this.ensureCurrentUserLoaded();
		this.getBoards();
	},
};
</script>

<style scoped>
#forum-management-container {
	background-color: #f5f5f5;
	min-height: calc(100svh - var(--header-height));
	padding: 0;
}

.forum-content {
	height: calc(100svh - var(--header-height));
	padding: 1.5rem;
	overflow: auto;
}

.forum-stats {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.stat-tile {
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 0.5rem;
	padding: 0.85rem 1rem;
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
}

.stat-label {
	font-size: 0.7rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	color: #6b7280;
	text-transform: uppercase;
}

.stat-value {
	font-size: 1.5rem;
	font-weight: 600;
	color: #111827;
	font-variant-numeric: tabular-nums;
}

@media (max-width: 600px) {
	.forum-stats {
		grid-template-columns: repeat(3, 1fr);
		gap: 0.5rem;
	}
	.stat-tile { padding: 0.6rem 0.7rem; }
	.stat-value { font-size: 1.2rem; }
}

.header-actions {
	display: flex;
	margin-bottom: 1.5rem;
	justify-content: space-between;
}

.search-container {
	flex: 1;
}

.search-input {
	width: 100%;
	padding: 0.75rem 1rem;
	border: 1px solid #e5e7eb;
	border-radius: 0.375rem;
	font-size: 0.875rem;
}

.table-container {
	margin-bottom: 1.5rem;
	border: 1px solid #e5e7eb;
	border-radius: 0.375rem;
	background: #ffffff;
}

table {
	width: 100%;
	border-collapse: collapse;
}

th, td {
	padding: 1rem;
	text-align: left;
	border-bottom: 1px solid #e5e7eb;
	font-size: 0.875rem;
}

th {
	background-color: #f9fafb;
	font-weight: 500;
	color: #374151;
}

tr:last-child td {
	border-bottom: none;
}

.board-name-cell {
	font-weight: 500;
	color: #111827;
}

.title-cell {
	max-width: 240px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-weight: 500;
	color: #111827;
}

.scope-pill {
	display: inline-block;
	padding: 2px 10px;
	border-radius: 999px;
	font-size: 0.75rem;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.02em;
}
.scope-pill.scope-global {
	background: rgba(3, 50, 77, 0.1);
	color: var(--secondary-color, #03324d);
}
.scope-pill.scope-productvariant {
	background: rgba(245, 158, 11, 0.12);
	color: #92400e;
}

.allow-on  { color: #059669; }
.allow-off { color: #9ca3af; }

.status-pill {
	display: inline-block;
	padding: 2px 10px;
	border-radius: 999px;
	font-size: 0.75rem;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.02em;
}
.status-pill.status-pill-active {
	background: rgba(5, 150, 105, 0.12);
	color: #047857;
}
.status-pill.status-pill-inactive {
	background: rgba(220, 38, 38, 0.12);
	color: #b91c1c;
}
.status-pill.status-pill-not-enabled {
	background: #f3f4f6;
	color: #6b7280;
}

.row-actions {
	display: flex;
	gap: 0.5rem;
	align-items: center;
	flex-wrap: wrap;
}

.open-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.4rem 0.9rem;
	border: 1px solid var(--primary-color, #ffd83f);
	border-radius: 0.375rem;
	background-color: #fff;
	font-size: 0.75rem;
	font-weight: 500;
	cursor: pointer;
	letter-spacing: 0.02em;
	color: #111827;
	transition: background-color 0.15s ease;
}
.open-btn:hover { background-color: #f9fafb; }
.open-btn i { font-size: 0.7rem; }

.enable-btn,
.disable-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.4rem 0.9rem;
	border-radius: 0.375rem;
	font-size: 0.75rem;
	font-weight: 500;
	cursor: pointer;
	letter-spacing: 0.02em;
	font-family: inherit;
	transition: background-color 0.15s ease, opacity 0.15s ease;
}
.enable-btn {
	border: 1px solid #047857;
	background-color: #ffffff;
	color: #047857;
}
.enable-btn:hover:not(:disabled) { background-color: #ecfdf5; }
.disable-btn {
	border: 1px solid #b91c1c;
	background-color: #ffffff;
	color: #b91c1c;
}
.disable-btn:hover:not(:disabled) { background-color: #fef2f2; }
.enable-btn:disabled,
.disable-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.enable-btn i,
.disable-btn i { font-size: 0.7rem; }

.no-data-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 30px 20px;
	color: #333;
	text-align: center;
}

.no-data-text {
	font-size: 16px;
	font-weight: 500;
	margin: 0;
}
</style>
